import { Badge, Button, Card, Group, Stack, Text, Switch } from '@mantine/core';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchBillingProvider, enableBillingProviderForCurrentCustomer, disableBillingProviderForCurrentCustomer } from '../services/BillingProvidersService';
import { fetchProviderMappings } from '../../provider-mappings/services/ProviderMappingsService';
import { useUserAccount } from '../../../../hooks/useUserAccount';
import { SimplePageLayout, SimplePageContentWrapper, SimplePageHeader, PlatformLoader } from '@flexinet/ui-components';
import { AiOutlinePlus } from 'react-icons/ai';
import { PaymentProfileCard } from '../../payment-profiles/components/PaymentProfileCard';
import { usePaymentProfiles } from '../../payment-profiles/hooks/usePaymentProfiles';
import { DetailPageNavigation } from '../navigation/DetailPageNavigation';

export const BillingProviderDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { userAccount } = useUserAccount();
    const { paymentProfiles, loading: profilesLoading, deletePaymentProfile } = usePaymentProfiles();

    const { data: provider, isLoading: providerLoading } = useQuery({
        queryKey: ['billing-provider', id],
        queryFn: () => fetchBillingProvider(id!),
        enabled: !!id
    });

    const { data: providerMappings, isLoading: mappingsLoading } = useQuery({
        queryKey: ['provider-mappings', userAccount?.customerId],
        queryFn: () => fetchProviderMappings({ localEntityId: userAccount?.customerId, entityType: 'Customer' }),
        enabled: !!userAccount?.customerId
    });

    const isEnabled = !!providerMappings?.find((m: any) =>
        m.billingProviderId === id || m.billingProvider?.id === id
    );

    const toggleMutation = useMutation({
        mutationFn: async () => {
            if (isEnabled) {
                return await disableBillingProviderForCurrentCustomer(id!);
            } else {
                return await enableBillingProviderForCurrentCustomer(id!);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['provider-mappings'] });
        }
    });

    const providerProfiles = paymentProfiles.filter(
        (profile) => profile.billingProviderId === id || profile.billingProvider?.id === id
    );

    if (providerLoading || profilesLoading || mappingsLoading) {
        return <PlatformLoader message="Loading provider details..." />;
    }

    if (!provider) {
        return (
            <SimplePageLayout
                pageHeader={<SimplePageHeader title="Provider Not Found" />}
                pageNavigation={<DetailPageNavigation />}
            >
                <SimplePageContentWrapper>
                    <Text c="dimmed" ta="center" py="xl">
                        The billing provider could not be found.
                    </Text>
                </SimplePageContentWrapper>
            </SimplePageLayout>
        );
    }

    return (
        <SimplePageLayout
            pageHeader={<SimplePageHeader title={provider.name || 'Provider Details'} />}
            pageNavigation={<DetailPageNavigation providerName={provider.name} />}
        >
            <SimplePageContentWrapper>
                <Stack gap="lg">
                    <Card withBorder p="lg">
                        <Stack gap="md">
                            <Group justify="space-between">
                                <Group gap="sm">
                                    <Text size="lg" fw={600}>{provider.name}</Text>
                                    <Badge color={isEnabled ? 'green' : 'gray'}>
                                        {isEnabled ? 'Enabled' : 'Disabled'}
                                    </Badge>
                                </Group>
                                <Switch
                                    checked={isEnabled}
                                    onChange={() => toggleMutation.mutate()}
                                    label={isEnabled ? 'Enabled' : 'Disabled'}
                                />
                            </Group>
                            {provider.description && (
                                <Text size="sm" c="dimmed">{provider.description}</Text>
                            )}
                        </Stack>
                    </Card>

                    <Group justify="space-between" align="center">
                        <Text size="lg" fw={600}>Payment Methods</Text>
                        <Button
                            leftSection={<AiOutlinePlus size={16} />}
                            onClick={() => navigate(`/billing/payment-method-setup?providerId=${provider.id}&providerKey=${provider.key}&returnTo=/billing/providers/${provider.id}`)}
                            disabled={!isEnabled}
                        >
                            Add Payment Method
                        </Button>
                    </Group>

                    {providerProfiles.length > 0 ? (
                        <Stack gap="sm">
                            {providerProfiles.map((profile) => (
                                <PaymentProfileCard
                                    key={profile.id}
                                    paymentProfile={profile}
                                    onDelete={deletePaymentProfile}
                                />
                            ))}
                        </Stack>
                    ) : (
                        <Text size="sm" c="dimmed" ta="center" py="lg">
                            No payment methods configured for this provider.
                        </Text>
                    )}
                </Stack>
            </SimplePageContentWrapper>
        </SimplePageLayout>
    );
};
